import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import type { Database } from "@/integrations/supabase/types";
import { getCurrentClinicCustomerId } from "@/lib/utils/clinic-context";
import { quotedIlikePattern } from "@/lib/utils/postgrest-filters";

type MedicationRow = Database["public"]["Tables"]["medications"]["Row"];
type MedicationInsert = Database["public"]["Tables"]["medications"]["Insert"];
type MedicationDosageRow = Database["public"]["Tables"]["medication_dosages"]["Row"];

interface UseMedicationsOptions {
  searchTerm?: string;
  includeControlled?: boolean;
  type?: 'custom' | 'principio_ativo' | 'product' | 'all';
  pharmaceuticalClassId?: string;
  speciesId?: string;
}

export const useMedications = ({
  searchTerm,
  includeControlled = true,
  type = 'all',
  pharmaceuticalClassId,
  speciesId
}: UseMedicationsOptions = {}) => {
  return useQuery({
    queryKey: ["medications", searchTerm, includeControlled, type, pharmaceuticalClassId, speciesId],
    queryFn: async () => {
      let query = supabase
        .from("medications")
        .select(`
          *,
          pharmaceutical_form:pharmaceutical_forms(id, name),
          administration_route:administration_routes(id, name),
          pharmaceutical_class:pharmaceutical_classes(id, name),
          brand:brands(id, name),
          active_principles:medication_active_principles!medication_id(
            active_principle:medications!active_principle_id(id, name)
          ),
          products:medication_active_principles!active_principle_id(
            product:medications!medication_id(id, name, brand:brands(name))
          ),
          target_species:medication_species(
            species:species(id, name)
          )
        `)
        .order("name", { ascending: true });

      if (searchTerm && searchTerm.length >= 2) {
        const p = quotedIlikePattern(searchTerm);
        query = query.or(`name.ilike.${p},manufacturer.ilike.${p},indication.ilike.${p}`);
      }

      if (type !== 'all') {
        query = query.eq("type", type);
      }

      if (pharmaceuticalClassId) {
        query = query.eq("pharmaceutical_class_id", pharmaceuticalClassId);
      }

      if (speciesId) {
        const { data: speciesLinks, error: speciesLinksError } = await supabase
          .from("medication_species")
          .select("medication_id")
          .eq("species_id", speciesId);
        if (speciesLinksError) throw speciesLinksError;
        const medicationIds = (speciesLinks ?? []).map((link) => link.medication_id);
        query = query.in("id", medicationIds);
      }

      if (!includeControlled) {
        query = query.eq("is_controlled", false);
      }

      const { data, error } = await query;
      if (error) throw error;
      return (data ?? []) as MedicationRow[];
    },
  });
};

export const useMedicationDosages = (medicationId?: string, speciesId?: string) => {
  return useQuery({
    queryKey: ["medication-dosages", medicationId, speciesId],
    enabled: Boolean(medicationId),
    queryFn: async () => {
      if (!medicationId) return [];

      let query = supabase
        .from("medication_dosages")
        .select(
          `
            *,
            med:medications(name, pharmaceutical_form, administration_route, dose_unit)
          `,
        )
        .eq("medication_id", medicationId)
        .order("species_id", { ascending: true });

      if (speciesId) {
        query = query.eq("species_id", speciesId);
      }

      const { data, error } = await query;
      if (error) throw error;
      return (data ?? []) as (MedicationDosageRow & {
        med: Pick<MedicationRow, "name" | "pharmaceutical_form" | "administration_route" | "dose_unit"> | null;
      })[];
    },
  });
};

export const useCreateMedication = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      payload: Omit<MedicationInsert, "customer_id" | "source" | "type" | "manufacturer"> & {
        customer_id?: string | null;
        type?: 'principio_ativo' | 'product' | 'custom';
        manufacturer?: string | null;
        activePrincipleId?: string; // Mantido para compatibilidade
        activePrincipleIds?: string[]; // Novo: suporta múltiplos
      }
    ) => {
      // Obter customer_id da clínica atual
      const customerId = await getCurrentClinicCustomerId();

      // Obter user_id do usuário autenticado
      const { data: userData, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;
      if (!userData.user?.id) throw new Error("Usuário não autenticado");

      const { activePrincipleId, activePrincipleIds, ...medicationPayload } = payload;

      const { data, error } = await supabase
        .from("medications")
        .insert({
          ...medicationPayload,
          name: medicationPayload.name.trim(),
          customer_id: customerId,
          created_by: userData.user.id,
          source: 'custom',
          type: payload.type || 'custom',
          manufacturer: payload.manufacturer || null,
        } as MedicationInsert & { created_by: string; source: string; type: string; manufacturer: string | null })
        .select()
        .single();

      if (error) throw error;

      // Determinar quais IDs de princípios ativos usar
      const principleIdsToLink = activePrincipleIds || (activePrincipleId ? [activePrincipleId] : []);

      // Se for produto e tiver princípios ativos, criar as relações
      if (data && (payload.type === 'product' || !payload.type) && principleIdsToLink.length > 0) {
        const relations = principleIdsToLink.map(id => ({
          medication_id: data.id,
          active_principle_id: id,
        }));

        const { error: relationError } = await supabase
          .from("medication_active_principles")
          .insert(relations);

        if (relationError) {
          console.error("Erro ao criar relação com princípio ativo:", relationError);
          // Não falhar a criação do medicamento se a relação falhar
        }
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["medications"] });
      toast.success("Medicamento cadastrado no catálogo");
    },
    onError: (error: any) => {
      // Tratar erro de constraint única (medicamento duplicado)
      if (error?.code === '23505' || error?.message?.includes('idx_medications_customer_name_form') || error?.message?.includes('duplicate key')) {
        toast.error("Medicamento já cadastrado");
        return;
      }
      console.error("Erro ao cadastrar medicamento:", error);
      toast.error("Não foi possível cadastrar o medicamento");
    },
  });
};
